import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  useMediaQuery,
  Badge,
} from "@mui/material";
import {
  Home as HomeIcon,
  Chat as ChatIcon,
  QuestionAnswer as QAIcon,
  Analytics as AnalyticsIcon,
  AdminPanelSettings as AdminIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Assessment as AssessmentIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  ArrowBack as ArrowBackIcon,
  Description as PaperIcon,
  Park as PulpIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { authService } from "../services/api";
import { JMC_COLORS } from "../styles/colors";
import { useUser } from "../pages/shared/context/userContext";
import DemoBanner from "./DemoBanner";

const TopNavBar = ({ user, showUserMenu = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down("md")); // tablet & mobile (below 960px)

  const currentUser = user || authService.getCurrentUser();
  const userEmail = currentUser?.email || "User";
  const userRole = currentUser?.role || "user";

  const { userDetails, setUserDetail, permissions } = useUser();

  const getDisplayName = () => {
    if (currentUser?.first_name || currentUser?.last_name) {
      return `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim();
    }
    return userEmail.split("@")[0];
  };

  const displayName = getDisplayName();
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const handleNavigation = (path, mode = null) => {
    if (mode) navigate(`/home?mode=${mode}`);
    else navigate(path);
    setDrawerOpen(false);
  };

  const handleLogout = async () => {
    await authService.logout();
    navigate("/");
  };

  const handleUserMenuOpen = (e) => setUserMenuAnchor(e.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);

  const isActive = (path) => location.pathname === path;
  const isActiveMode = (mode) => location.search.includes(`mode=${mode}`);

  // Show back button on inner pages (not on home or landing)
  const showBackButton = location.pathname !== '/home' && location.pathname !== '/' && location.pathname !== '/login';

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/home');
    }
  };

  const navItems = [
    { label: "Home", icon: <DashboardIcon />, path: "/home", color: "#0C4DA2" },
    { label: "Chat", icon: <ChatIcon />, mode: "chat", color: "#10b981" },
    { label: "Q&A", icon: <QAIcon />, mode: "qa", color: "#8b5cf6" },
    userDetails?.role === "admin" && {
      label: "Cost Analytics",
      icon: <AssessmentIcon />,
      path: "/analytics-dashboard",
      color: "#f59e0b"
    },
    { label: "Paper Mill", icon: <PaperIcon />, path: "/paper-mill-dashboard", color: "#0891b2" },
    { label: "Pulp Mill", icon: <PulpIcon />, path: "/pulp-mill-dashboard", color: "#059669" },
    { label: "Maintenance", icon: <BuildIcon />, path: "/maintenance-dashboard", color: "#dc2626" },
  ].filter(Boolean);


  return (
    <>
      {/* 🔹 Top Navbar */}
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: "#fff",
          borderBottom: "1px solid",
          borderColor: "divider",
          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ display: "flex", justifyContent: "space-between", px: 1 }}>
            {/* 🔹 Left Section: Back Button / Logo / Hamburger */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* Back button for inner pages on mobile */}
              {isTabletOrMobile && showBackButton && (
                <IconButton onClick={handleBack} sx={{ mr: 0.5 }}>
                  <ArrowBackIcon sx={{ color: "#0C4DA2" }} />
                </IconButton>
              )}
              {isTabletOrMobile && !showBackButton && (
                <IconButton onClick={() => setDrawerOpen(true)}>
                  <MenuIcon sx={{ color: "#0C4DA2" }} />
                </IconButton>
              )}
              {isTabletOrMobile && showBackButton && (
                <IconButton onClick={() => setDrawerOpen(true)}>
                  <MenuIcon sx={{ color: "#0C4DA2" }} />
                </IconButton>
              )}

              <Typography
                variant="h6"
                sx={{
                  color: "#0C4DA2",
                  fontWeight: 700,
                  ml: 1,
                  cursor: "pointer",
                }}
                onClick={() => handleNavigation("/home")}
              >
                <Box component="img" src="/mill_logo.svg" alt="Mill Logo" sx={{ height: 30 }} />
              </Typography>
            </Box>

            {/* 🔹 Desktop Navigation Buttons */}
            {!isTabletOrMobile && (
              <Box sx={{ display: "flex", gap: 1, flex: 1, justifyContent: "center" }}>
                {navItems.map((item) => {
                  const isItemActive = (item.mode && isActiveMode(item.mode)) || isActive(item.path);
                  return (
                    <Button
                      key={item.label}
                      startIcon={React.cloneElement(item.icon, {
                        sx: { color: isItemActive ? item.color : "#777" }
                      })}
                      onClick={() =>
                        item.mode
                          ? handleNavigation("/home", item.mode)
                          : handleNavigation(item.path)
                      }
                      sx={{
                        color: isItemActive ? item.color : "#555",
                        backgroundColor: isItemActive
                          ? `${item.color}15`
                          : "transparent",
                        textTransform: "none",
                        fontWeight: isItemActive ? 600 : 500,
                        borderBottom: isItemActive ? `2px solid ${item.color}` : "2px solid transparent",
                        borderRadius: 0,
                        px: 1.5,
                        "&:hover": {
                          backgroundColor: `${item.color}10`,
                          color: item.color,
                          "& .MuiSvgIcon-root": { color: item.color }
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Box>
            )}


            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

              {/* Alert Menu */}
              <Badge
                badgeContent="0"
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.75rem',
                    height: 18,
                    minWidth: 18
                  }
                }}
              >
                <IconButton
                  disableRipple
                  sx={{
                    padding: 0,
                    borderRadius: "4px",
                    width: "fit-content",
                    height: "fit-content",
                    minWidth: "unset !important",
                    minHeight: "unset !important",
                    color: JMC_COLORS.text.primary,
                    '&:hover': { color: JMC_COLORS.primary }
                  }}
                >
                  <NotificationsIcon fontSize="small" />
                </IconButton>
              </Badge>


              {/* 🔹 Right Side: User Menu */}
              {showUserMenu && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Button
                    onClick={handleUserMenuOpen}
                    endIcon={<ArrowDropDownIcon />}
                    sx={{
                      textTransform: "none",
                      color: "#333",
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: "#0C4DA2",
                        fontSize: "0.875rem",
                        mr: 1,
                      }}
                    >
                      {initials}
                    </Avatar>
                    <Box sx={{ display: { xs: "none", md: "block" } }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {displayName}
                      </Typography>
                    </Box>
                  </Button>

                  {/* 🔹 User Dropdown Menu */}
                  <Menu
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={handleUserMenuClose}
                    PaperProps={{
                      sx: {
                        mt: 1.5,
                        minWidth: 200,
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      },
                    }}
                  >
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {userEmail}
                      </Typography>
                    </Box>
                    <Divider />
                    <MenuItem onClick={() => handleNavigation("/home")}>
                      <ListItemIcon>
                        <DashboardIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Home" />
                    </MenuItem>
                    {userRole === "admin" && (
                      <MenuItem onClick={() => handleNavigation("/admin")}>
                        <ListItemIcon>
                          <AdminIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Admin Panel" />
                      </MenuItem>
                    )}
                    <MenuItem onClick={() => { handleUserMenuClose(); navigate('/profile'); }}>
                      <ListItemIcon>
                        <PersonIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Profile" />
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Logout" />
                    </MenuItem>
                  </Menu>
                </Box>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* 🔹 Demo Mode Banner */}
      <DemoBanner />

      {/* 🔹 Drawer for Tablet + Mobile */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 270,
            borderTopRightRadius: 20,
            borderBottomRightRadius: 20,
            background: "linear-gradient(135deg, #f5f7fa, #dbeafe)",
            boxShadow: "5px 0 25px rgba(0,0,0,0.1)",
          },
        }}
        ModalProps={{
          sx: {
            backdropFilter: "blur(3px)",
            backgroundColor: "rgba(0,0,0,0.3)",
          },
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: "1px solid rgba(0,0,0,0.1)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0C4DA2" }}>
            Menu
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)}>
            <CloseIcon sx={{ color: "#0C4DA2" }} />
          </IconButton>
        </Box>

        {/* Drawer Content */}
        <Box sx={{ p: 2 }}>
          <List>
            {navItems.map((item) => {
              const isItemActive = (item.mode && isActiveMode(item.mode)) || isActive(item.path);
              return (
                <ListItem key={item.label} disablePadding>
                  <ListItemButton
                    onClick={() =>
                      item.mode
                        ? handleNavigation("/home", item.mode)
                        : handleNavigation(item.path)
                    }
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      borderLeft: `3px solid ${isItemActive ? item.color : 'transparent'}`,
                      backgroundColor: isItemActive ? `${item.color}15` : "transparent",
                      "&:hover": {
                        backgroundColor: `${item.color}20`,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: item.color, minWidth: 40 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: isItemActive ? 700 : 600,
                        color: isItemActive ? item.color : "#333",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ my: 1.5 }} />

          <Button
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: "#0C4DA2",
              borderRadius: 2,
              "&:hover": { backgroundColor: "#083b7a" },
            }}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default TopNavBar;
